import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  createEmptyNav,
  createPartsNav,
  createPositionsNav,
  createScopedNav,
} from "../../common/slices/specsEditor/helpers";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import type { SpecsModel } from "./specsModel/SpecsModel";
import type {
  SpecsEditorNav,
  SpecsEditorSession,
  SpecsLayer,
  SpecsLayerModelPath,
} from "./types";
import type { PartModel } from "./specsModel/PartModel";
import { useSpecsEditorLayerModelPaths } from "./useSpecsEditorLayerModelPaths";

type ScopedNav = Extract<
  SpecsEditorNav,
  { kind: "element" } | { kind: "layer" } | { kind: "positions" }
>;

type UseSpecsEditorPartsArgs = {
  currentVersion: ReturnType<SpecsModel["getVersion"]> | null;
  layers: SpecsLayer[];
  selectedNav: SpecsEditorSession["nav"];
  session: SpecsEditorSession;
};

function getNavForPart(
  nav: SpecsEditorNav,
  partName: string
): ScopedNav | null {
  switch (nav.kind) {
    case "element":
    case "layer":
      return nav.part === partName ? nav : null;
    case "positions":
      return nav.part === partName && nav.element ? nav : null;
    default:
      return null;
  }
}

function getNavForElement(
  nav: SpecsEditorNav,
  partName: string,
  elementName: string
): ScopedNav | null {
  switch (nav.kind) {
    case "element":
    case "layer":
    case "positions":
      return nav.part === partName && nav.element === elementName ? nav : null;
    default:
      return null;
  }
}

function createNavForRenamedPart(args: {
  nav: SpecsEditorNav;
  session: SpecsEditorSession;
  previousPartName: string;
  nextPartName: string;
}) {
  const { nav, session, previousPartName, nextPartName } = args;

  if (nav.kind === "parts" && nav.part === previousPartName) {
    return createPartsNav(session, nextPartName);
  }

  const scopedNav = getNavForPart(nav, previousPartName);
  if (!scopedNav) {
    return null;
  }

  if (scopedNav.kind === "positions") {
    return createPositionsNav(session, {
      positionId: scopedNav.positionId,
      partName: nextPartName,
      elementName: scopedNav.element,
      styleName: scopedNav.style,
      layerId: scopedNav.layerId,
    });
  }

  return createScopedNav(
    session,
    nextPartName,
    scopedNav.element,
    scopedNav.style
  );
}

function createNavForRemovedPart(args: {
  nav: SpecsEditorNav;
  session: SpecsEditorSession;
  removedPartName: string;
  nextPartName: string | undefined;
}) {
  const { nav, session, removedPartName, nextPartName } = args;
  const shouldLeaveCurrentNav =
    nav.kind === "parts" || getNavForPart(nav, removedPartName) !== null;

  if (!shouldLeaveCurrentNav) {
    return null;
  }

  return nextPartName
    ? createPartsNav(session, nextPartName)
    : createEmptyNav(session);
}

function createNavForMovedElement(args: {
  nav: SpecsEditorNav;
  session: SpecsEditorSession;
  previousPartName: string;
  elementName: string;
  nextPartName: string;
}) {
  const { nav, session, previousPartName, elementName, nextPartName } = args;

  const scopedNav = getNavForElement(nav, previousPartName, elementName);
  if (!scopedNav) {
    return null;
  }

  if (scopedNav.kind === "positions") {
    return createPositionsNav(session, {
      positionId: scopedNav.positionId,
      partName: nextPartName,
      elementName,
      styleName: scopedNav.style,
      layerId: scopedNav.layerId,
    });
  }

  return createScopedNav(session, nextPartName, elementName, scopedNav.style);
}

function renameLayerPart(
  path: SpecsLayerModelPath | null,
  previousPartName: string,
  nextPartName: string
) {
  if (!path || path.partName !== previousPartName) {
    return path;
  }

  return { ...path, partName: nextPartName };
}

function clearLayerPart(path: SpecsLayerModelPath | null, partName: string) {
  return path?.partName === partName ? null : path;
}

function moveLayerElementPart(args: {
  path: SpecsLayerModelPath | null;
  previousPartName: string;
  elementName: string;
  nextPartName: string;
}) {
  const { path, previousPartName, elementName, nextPartName } = args;

  if (!path || path.partName !== previousPartName) {
    return path;
  }

  if (path.elementName !== elementName) {
    return path;
  }

  return { ...path, partName: nextPartName };
}

export function useSpecsEditorParts({
  currentVersion,
  layers,
  selectedNav,
  session,
}: UseSpecsEditorPartsArgs) {
  const dispatch = useDispatch();
  const updateLayerModelPaths = useSpecsEditorLayerModelPaths(layers);

  const addPart = useCallback(
    (name: string) => {
      if (currentVersion?.addPart(name)) {
        dispatch(specsEditorActions.selectParts(name.trim()));
      }
    },
    [currentVersion, dispatch]
  );

  const updatePart = useCallback(
    (
      partName: string,
      patch: Partial<
        Pick<PartModel, "mainElement" | "required" | "inheritFrom">
      >
    ) => {
      currentVersion?.updatePart(partName, patch);
    },
    [currentVersion]
  );

  const renamePart = useCallback(
    (partName: string, nextPartName: string) => {
      const trimmedName = nextPartName.trim();
      if (!currentVersion?.renamePart(partName, trimmedName)) {
        return;
      }

      updateLayerModelPaths((path) =>
        renameLayerPart(path, partName, trimmedName)
      );
      const nextNav = createNavForRenamedPart({
        nav: selectedNav,
        session,
        previousPartName: partName,
        nextPartName: trimmedName,
      });

      if (nextNav) {
        dispatch(specsEditorActions.setNav(nextNav));
      }
    },
    [currentVersion, dispatch, selectedNav, session, updateLayerModelPaths]
  );

  const removePart = useCallback(
    (partName: string) => {
      if (!currentVersion?.removePart(partName)) {
        return;
      }

      const nextPartName = currentVersion.parts[0]?.name;
      updateLayerModelPaths((path) => clearLayerPart(path, partName));
      const nextNav = createNavForRemovedPart({
        nav: selectedNav,
        session,
        removedPartName: partName,
        nextPartName,
      });

      if (nextNav) {
        dispatch(specsEditorActions.setNav(nextNav));
      }
    },
    [currentVersion, dispatch, selectedNav, session, updateLayerModelPaths]
  );

  const reorderPart = useCallback(
    (partName: string, targetIndex: number) => {
      currentVersion?.reorderPart(partName, targetIndex);
    },
    [currentVersion]
  );

  const moveElementToPart = useCallback(
    (fromPartName: string, elementName: string, toPartName: string) => {
      if (
        !currentVersion?.moveElementToPart(
          fromPartName,
          elementName,
          toPartName
        )
      ) {
        return;
      }

      updateLayerModelPaths((path) =>
        moveLayerElementPart({
          path,
          previousPartName: fromPartName,
          elementName,
          nextPartName: toPartName,
        })
      );
      const nextNav = createNavForMovedElement({
        nav: selectedNav,
        session,
        previousPartName: fromPartName,
        elementName,
        nextPartName: toPartName,
      });

      if (nextNav) {
        dispatch(specsEditorActions.setNav(nextNav));
      }
    },
    [currentVersion, dispatch, selectedNav, session, updateLayerModelPaths]
  );

  return {
    addPart,
    updatePart,
    renamePart,
    removePart,
    reorderPart,
    moveElementToPart,
  };
}
